import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Progress,
} from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi, configApiRef } from '@backstage/core-plugin-api';
import { 
  IconButton, 
  Tooltip, 
  Snackbar, 
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  LinearProgress,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import ShareIcon from '@material-ui/icons/Share'
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';

interface GcsFile {
  name: string;
  path: string;
}

interface SearchResponse {
  found: boolean;
  item?: GcsFile;
}

const useStyles = makeStyles({
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  pointer: {
    cursor: 'pointer',
  },
  container: {
    marginTop: '16px',
  },
  searchBox: {
    marginBottom: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  infoText: {
    fontSize: '14px',
    color: '#666',
  },
});

export const GcsFileViewer = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [files, setFiles] = useState<GcsFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  const [searchFilter, setSearchFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const latestFetchIdRef = useRef(0);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchFilter);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchFilter]);

  const fetchExactMatch = useCallback(async (search: string) => {
    const fetchId = ++latestFetchIdRef.current;
    try {
      setLoading(true);
      const backendUrl = await discoveryApi.getBaseUrl('gcs-proxy');

      // Exact-filename lookup: single result at most, no pagination.
      const queryParams = new URLSearchParams({ search: search.trim() });
      const url = `${backendUrl}/list-files?${queryParams.toString()}`;
      const response = await fetchApi.fetch(url);

      if (!response.ok) throw new Error('Failed to fetch files');

      const data: SearchResponse = await response.json();

      if (fetchId !== latestFetchIdRef.current) {
        return;
      }

      setFiles(data.found && data.item ? [data.item] : []);
      setError(null);
    } catch (err: any) {
      if (fetchId === latestFetchIdRef.current) {
        setError(err);
        setFiles([]);
      }
    } finally {
      if (fetchId === latestFetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    if (!debouncedSearch.trim()) {
      // Nothing to search yet: don't hit the backend and don't show a
      // loading indicator until the user actually types a filename.
      latestFetchIdRef.current += 1;
      setFiles([]);
      setError(null);
      setLoading(false);
      return;
    }
    fetchExactMatch(debouncedSearch);
  }, [debouncedSearch, fetchExactMatch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilter(event.target.value);
  };
  
  const handleDownload = async (path: string) => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('gcs-proxy');
      const fileName = path.split('/').pop();
      
      window.open(`${baseUrl}/view/${fileName}`, '_blank');
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to download file', severity: 'error' });
    }
  };

  const handleCopyPublicLink = async (path: string) => {
    try {
      const fileName = path.split('/').pop();
      const appUrl = configApi.getString('app.baseUrl');
      const isPre = appUrl.includes('-pre') || appUrl.includes('localhost');
      
      const domain = isPre ? 'doc-pre.allfunds.com' : 'doc.allfunds.com';
      const prettyUrl = `https://${domain}/funddata/${fileName}`;

      await navigator.clipboard.writeText(prettyUrl);
      setSnackbar({ open: true, message: 'Public share link copied!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to copy link', severity: 'error' });
    }
  };

  if (error && !files.length) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  const isSearchMode = !!debouncedSearch.trim();

  return (
    <div className={classes.container}>
      <Box className={classes.header}>
        <h2 style={{ margin: 0 }}>FundData Legal Files</h2>
      </Box>

      <TextField
        fullWidth
        placeholder="Search by exact filename (e.g. 0003f6791a75a53909a26a4ced38ebab or 0003f6791a75a53909a26a4ced38ebab.pdf)"
        value={searchFilter}
        onChange={handleSearchChange}
        className={classes.searchBox}
        variant="outlined"
        size="small"
      />

      {loading && files.length > 0 && (
        <LinearProgress style={{ marginBottom: '8px', borderRadius: '4px' }} />
      )}

      {!isSearchMode ? (
        <Box className={classes.infoText} style={{ padding: '16px 0' }}>
          Type an exact filename above to search.
        </Box>
      ) : loading && files.length === 0 ? (
        <Progress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>File Path</TableCell>
                <TableCell align="right" style={{ width: '160px' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              style={{
                opacity: loading ? 0.4 : 1,
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              {files.length > 0 ? (
                files.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Link 
                        className={classes.pointer} 
                        onClick={() => handleDownload(row.path)}
                      >
                        {row.name}
                      </Link>
                    </TableCell>
                    <TableCell>{row.path}</TableCell>
                    <TableCell align="right">
                      <div className={classes.actionContainer}>
                        <Tooltip title="Download">
                          <IconButton 
                            onClick={() => handleDownload(row.path)} 
                            color="primary"
                            size="small"
                          >
                            <GetAppIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Share Public Link">
                          <IconButton 
                            onClick={() => handleCopyPublicLink(row.path)} 
                            color="secondary"
                            size="small"
                          >
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" style={{ padding: '32px' }}>
                    No file found with that exact name
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};
